import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

type IncompleteAdminProfileContextValue = {
    /** True while first-time admin must finish /add-admin-profile before using the app. */
    setupIncomplete: boolean;
    setSetupIncomplete: (incomplete: boolean) => void;
    /** Registered by ProfileForm to show inline validation. */
    triggerValidation: () => void;
    registerValidationHandler: (handler: (() => void) | null) => void;
};

const IncompleteAdminProfileContext = createContext<IncompleteAdminProfileContextValue | null>(null);

export function IncompleteAdminProfileProvider({ children }: { children: React.ReactNode }) {
    const [setupIncomplete, setSetupIncomplete] = useState(false);
    const validationHandlerRef = useRef<(() => void) | null>(null);

    const registerValidationHandler = useCallback((handler: (() => void) | null) => {
        validationHandlerRef.current = handler;
    }, []);

    const triggerValidation = useCallback(() => {
        validationHandlerRef.current?.();
    }, []);

    const value = useMemo(
        () => ({
            setupIncomplete,
            setSetupIncomplete,
            triggerValidation,
            registerValidationHandler,
        }),
        [setupIncomplete, triggerValidation, registerValidationHandler],
    );

    return (
        <IncompleteAdminProfileContext.Provider value={value}>
            {children}
        </IncompleteAdminProfileContext.Provider>
    );
}

export function useIncompleteAdminProfile() {
    const ctx = useContext(IncompleteAdminProfileContext);
    if (!ctx) {
        throw new Error("useIncompleteAdminProfile must be used within IncompleteAdminProfileProvider");
    }
    return ctx;
}

/** Safe for Sidebar when provider may be absent (e.g. tests). */
export function useIncompleteAdminProfileOptional() {
    return useContext(IncompleteAdminProfileContext);
}
